import {
  Component,
  createMemo,
  createSignal,
  createEffect,
  For,
  Show,
  onMount,
  onCleanup,
  untrack,
} from 'solid-js';
import {
  MODUS_TAG_EDITOR_INPUT_SHADOW_STYLESHEET,
  MODUS_TAG_EDITOR_INPUT_STYLESHEET,
} from '../../mc-custom-css';
import { readModusInputChangeValue } from '../../utils/modus-input-change';
import CustomTooltip from '../CustomTooltip/CustomTooltip';
import { Chip } from '../Chips/Chips';
import { injectInRenderContext, injectShadowStylesWhenReady } from '../util';

// Tag model interfaces (reused from original)
export enum Separators {
  ENTER = '13',
  COMMA = '44',
  SPACE = '32',
  PIPE = '124',
  MINUS = '45',
  SLASH = '47',
  SEMICOLON = '59',
}

export interface ITagItem {
  label: string;
  value: string;
  isValid: boolean;
  id?: string;
  color?: string;
  tiduuid?: string;
}

export enum SearchBy {
  LABEL = 'LABEL',
  VALUE = 'VALUE',
  BOTH = 'BOTH',
}

export interface IInputCount {
  selectedCount?: number;
  totalCount: number;
}

export interface TagEditorProps {
  separator?: Separators[];
  selectedTagList?: ITagItem[];
  matchCase?: boolean;
  title?: string;
  placeHolder?: string;
  validateRx?: RegExp;
  invalidErrorMsg?: string;
  notFoundErrorMsg?: string;
  tagList?: ITagItem[];
  searchBy?: SearchBy;
  allowCustomTag?: boolean;
  setFocus?: boolean;
  id?: string;
  customErrorMessage?: string;
  matchBy?: SearchBy;
  autoSave?: boolean;
  enableSearchIcon?: boolean;
  predefinedTagList?: ITagItem[];
  tagListTitle?: string;
  maxLength?: number;
  maxLengthErrorMessage?: string;
  inputCount?: IInputCount;
  limitErrorMessage?: string;
  // Callback props for event handling
  onTagChange?(tags: ITagItem[]): void;
  onTypeText?(query: string): void;
  onSearchClose?(): void;
}

// Highlights the matched search key inside source text
const InputChips: Component<{
  tags: ITagItem[];
  onRemoveTag(index: number): void;
}> = (props) => {
  return (
    <For each={props.tags}>
      {(tag, index) => (
        <li class="mc-tag-editor-chip-item">
          <CustomTooltip
            text={tag.label}
            position="left"
            class="mc-tag-editor-chip-tooltip-host"
          >
            <Chip
              label={tag.label}
              showRemove={true}
              hasError={!tag.isValid}
              multiline={false}
              size="md"
              shape="circle"
              onRemove={() => {
                props.onRemoveTag(index());
              }}
            />
          </CustomTooltip>
        </li>
      )}
    </For>
  );
};

// Highlights the matched search key inside source text
const HighlightText: Component<{
  source: string;
  search: string;
  matchCase?: boolean;
}> = (textProps) => {
  const highlight = createMemo(() => {
    if (!textProps.search) return null;
    const src = !textProps.matchCase
      ? textProps.source.toLowerCase()
      : textProps.source;
    const key = !textProps.matchCase
      ? textProps.search.toLowerCase()
      : textProps.search;
    const index = src.indexOf(key);
    if (index === -1) return null;
    return {
      before: textProps.source.substring(0, index),
      match: textProps.source.substring(index, index + textProps.search.length),
      after: textProps.source.substring(index + textProps.search.length),
    };
  });

  return (
    <Show when={highlight()} fallback={textProps.source}>
      {(h) => (
        <>
          {h().before}
          <strong textContent={h().match} />
          {h().after}
        </>
      )}
    </Show>
  );
};

// Simple TagItems component for SolidJS
const TagItems: Component<{
  tags: ITagItem[];
  activeIndex: number;
  search: string;
  onAddTag(tag: ITagItem): void;
  isLoading: boolean;
  notFoundErrorMsg?: string;
  matchCase?: boolean;
  enableSearchIcon?: boolean;
  tagListTitle?: string;
}> = (props) => {
  return (
    <div
      class={`auto-complete ${
        props.enableSearchIcon ? 'te-top-fix tags-dd-fullheigth' : ''
      }`}
    >
      <ul class="mw-100">
        <Show when={props.isLoading}>
          <li>
            <div class="p-4 flex justify-center">
              <modus-wc-loader variant="spinner" size="sm" />
            </div>
          </li>
        </Show>
        <Show when={!props.isLoading && props.tags.length === 0}>
          <li>
            <div class="select-menu-item no-results">
              <modus-wc-typography
                hierarchy="p"
                size="md"
                label={props.notFoundErrorMsg || 'No results found'}
              />
            </div>
          </li>
        </Show>
        <Show when={!props.isLoading && props.tags.length > 0}>
          <Show when={props.tagListTitle}>
            <li>
              <div class="px-3 py-2 text-xs font-semibold text-gray-600 border-b">
                {props.tagListTitle}
              </div>
            </li>
          </Show>
          <For each={props.tags}>
            {(tag, index) => (
              <li>
                <div
                  class={`select-menu-item ${
                    props.activeIndex === index() ? 'bg-gray_0' : ''
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    props.onAddTag(tag);
                  }}
                >
                  <modus-wc-typography hierarchy="p" size="sm">
                    <span class="text-ellipsis" title={tag.label}>
                      <HighlightText
                        source={tag.label}
                        search={props.search}
                        matchCase={props.matchCase}
                      />
                    </span>
                  </modus-wc-typography>
                </div>
              </li>
            )}
          </For>
        </Show>
      </ul>
    </div>
  );
};

const TagEditor: Component<TagEditorProps> = (props) => {
  let hostRef: HTMLDivElement | undefined;
  let modusInputWrapper: HTMLDivElement | undefined;
  // Reactive getters for props
  const getSeparator = (): Separators[] =>
    props.separator || [Separators.ENTER];
  const getSelectedTagList = (): ITagItem[] => props.selectedTagList || [];
  const getMatchCase = (): boolean => props.matchCase ?? true;
  const getSearchBy = (): SearchBy => props.searchBy || SearchBy.LABEL;
  const getMatchBy = (): SearchBy => props.matchBy || SearchBy.VALUE;
  const getAllowCustomTag = (): boolean => props.allowCustomTag || false;
  const getNotFoundErrorMsg = (): string =>
    props.notFoundErrorMsg || 'No results found';

  const taglistValue = untrack(() => props.tagList) || [];

  // State signals
  const [searchKey, setSearchKey] = createSignal('');
  const [errMessage, setErrMessage] = createSignal('');
  const [loading] = createSignal(false);
  const [tags, setTags] = createSignal<ITagItem[]>(taglistValue);
  const [tagActiveIndex, setTagActiveIndex] = createSignal(-1);
  const [showSetFocusLine, setShowSetFocusLine] = createSignal(false);

  let detachInputListeners: (() => void) | undefined;

  const getModusInputHost = (): (HTMLElement & { focus?(): void }) | null => {
    return modusInputWrapper?.querySelector('modus-wc-text-input') as
      | (HTMLElement & { focus?(): void })
      | null;
  };

  const getInnerInput = (): HTMLInputElement | null => {
    const host = getModusInputHost();
    if (!host) {
      return null;
    }
    return (
      (host.shadowRoot?.querySelector('input') as HTMLInputElement | null) ??
      (host.querySelector('input') as HTMLInputElement | null)
    );
  };

  const getCurrentInputValue = (): string => {
    const fromInner = getInnerInput()?.value;
    if (typeof fromInner === 'string') {
      return fromInner;
    }
    return searchKey();
  };

  const syncSearchKeyFromInput = (): string => {
    const nextValue = getCurrentInputValue();
    setSearchKey(nextValue);
    return nextValue;
  };

  const setupModusInput = (): void => {
    if (!hostRef) {
      return;
    }
    injectInRenderContext(
      hostRef,
      'mc-tag-editor-modus-input',
      MODUS_TAG_EDITOR_INPUT_STYLESHEET
    );
    const inputHost = getModusInputHost();
    if (inputHost) {
      injectShadowStylesWhenReady(
        inputHost,
        'mc-tag-editor-modus-input-inner',
        MODUS_TAG_EDITOR_INPUT_SHADOW_STYLESHEET
      );
    }
    bindNativeInputListeners();
  };

  const focusTagEditorInput = (attempt = 0, maxAttempts = 40): void => {
    setupModusInput();
    const input = getInnerInput();
    if (input) {
      input.focus();
      getModusInputHost()?.focus?.();
      return;
    }
    if (attempt < maxAttempts) {
      requestAnimationFrame(() =>
        focusTagEditorInput(attempt + 1, maxAttempts)
      );
    }
  };

  const bindNativeInputListeners = (): void => {
    detachInputListeners?.();
    const host = getModusInputHost();
    if (!host) {
      return;
    }

    const onKeyDown = (e: KeyboardEvent): void => {
      handleKeyDown(e);
    };
    const onPaste = (e: ClipboardEvent): void => {
      handlePaste(e);
    };

    host.addEventListener('keydown', onKeyDown);
    host.addEventListener('paste', onPaste);
    detachInputListeners = () => {
      host.removeEventListener('keydown', onKeyDown);
      host.removeEventListener('paste', onPaste);
    };
  };

  // Update tags when tagList prop changes
  createEffect(() => {
    if (props.tagList) {
      setTags(props.tagList);
    }
  });

  // Helper functions
  const getSearchFrom = (tag: ITagItem): string => {
    const matchCase = getMatchCase();
    const searchBy = getSearchBy();

    if (searchBy === SearchBy.VALUE) {
      return !matchCase ? tag.value.toLowerCase() : tag.value;
    } else if (searchBy === SearchBy.BOTH) {
      const value = tag.label + ' ' + tag.value;
      return !matchCase ? value.toLowerCase() : value;
    }
    return !matchCase ? tag.label.toLowerCase() : tag.label;
  };

  const getTagOptions = (searchOverride?: string): ITagItem[] => {
    const currentSearchKey = searchOverride ?? searchKey();
    const currentTags = tags();
    const matchCase = getMatchCase();

    // Convert search key to lowercase if case-insensitive
    const processedSearchKey = !matchCase
      ? currentSearchKey.toLowerCase()
      : currentSearchKey;

    let tagOptions = [
      ...currentTags.filter((t: ITagItem) => {
        // getSearchFrom already handles case conversion based on matchCase
        const searchableText = getSearchFrom(t);
        return searchableText.includes(processedSearchKey);
      }),
    ];

    tagOptions = tagOptions.filter(
      ({ value: v1 }: ITagItem) =>
        !getSelectedTagList().some(({ value: v2 }: ITagItem) => v1 === v2)
    );

    return tagOptions;
  };

  const onTagChange = (newTags: ITagItem[]): void => {
    const totalInputCount = props.inputCount?.totalCount;

    // Call callback if provided
    if (props.onTagChange) {
      props.onTagChange(newTags);
    }

    if (totalInputCount && newTags.length > totalInputCount) {
      setSearchKey('');
      setErrMessage(props.limitErrorMessage || '');
    } else {
      setSearchKey('');
      setErrMessage('');
    }
    setTagActiveIndex(-1);
  };

  const onAddTag = (tag: ITagItem): void => {
    addTags(tag);
  };

  const addTags = (tag: ITagItem): void => {
    const selectedTagList = getSelectedTagList();
    const matchBy = getMatchBy();

    const duplicateTags = selectedTagList.filter((selectedTag) => {
      if (matchBy === SearchBy.VALUE) {
        return selectedTag.value === tag.value;
      } else if (matchBy === SearchBy.LABEL) {
        return selectedTag.label === tag.label;
      } else {
        return (
          selectedTag.label === tag.label || selectedTag.value === tag.value
        );
      }
    });

    if (duplicateTags.length === 0) {
      const newTags: ITagItem[] = [...selectedTagList, tag];
      onTagChange(newTags);
    }
  };

  const saveTag = (queryOverride?: string): void => {
    const currentSearchKey = (queryOverride ?? searchKey()).trim();
    const currentTagActiveIndex = tagActiveIndex();

    if (!currentSearchKey) {
      return;
    }

    const options = getTagOptions(currentSearchKey);

    if (options.length > 0 && currentTagActiveIndex >= 0) {
      addTags(options[currentTagActiveIndex]);
      return;
    }

    if (getAllowCustomTag() && currentTagActiveIndex === -1) {
      if (props.validateRx && !props.validateRx.test(currentSearchKey)) {
        setErrMessage(props.invalidErrorMsg || '');
        return;
      }
      const tag: ITagItem = {
        label: currentSearchKey,
        value: currentSearchKey,
        isValid: true,
      };
      addTags(tag);
    }
  };

  const onRemoveTag = (index: number): void => {
    const newTags = [...getSelectedTagList()];
    newTags.splice(index, 1);
    onTagChange([...newTags]);
  };

  const handleModusInputChange = (e: Event): void => {
    let query = readModusInputChangeValue(e);
    if (!query) {
      query = getCurrentInputValue();
    }

    if (query.length > 0) {
      const lastChar = query
        .charCodeAt(query.length - 1)
        .toString() as Separators;
      if (getSeparator().includes(lastChar)) {
        const trimmed = query.slice(0, -1).trim();
        setSearchKey('');
        setErrMessage('');
        if (trimmed) {
          saveTag(trimmed);
        }
        return;
      }
    }

    if (props.onTypeText) {
      props.onTypeText(query);
    }

    setSearchKey(query);
    setErrMessage('');
  };

  const isSeparatorKeyEvent = (e: KeyboardEvent): boolean => {
    const separators = getSeparator();
    if (e.key === 'Enter' && separators.includes(Separators.ENTER)) {
      return true;
    }
    const charCode = e.key.length === 1 ? e.key.charCodeAt(0) : 0;
    return separators.includes(charCode.toString() as Separators);
  };

  const handleSeparatorKey = (e: KeyboardEvent): void => {
    if (!isSeparatorKeyEvent(e)) {
      return;
    }

    const currentSearchKey = syncSearchKeyFromInput().trim();
    if (currentSearchKey.length === 0) {
      return;
    }

    saveTag(currentSearchKey);
    e.preventDefault();
  };

  const handleKeyDown = (e: KeyboardEvent): void => {
    if (isSeparatorKeyEvent(e)) {
      handleSeparatorKey(e);
      return;
    }

    if (e.key === 'Backspace' && getCurrentInputValue().length === 0) {
      const newTags = [...getSelectedTagList()];
      newTags.pop();
      onTagChange(newTags);
    }

    const currentTagActiveIndex = tagActiveIndex();
    const tagOptions = getTagOptions();

    if (tagOptions.length > 0) {
      if (
        e.key === 'ArrowDown' &&
        currentTagActiveIndex < tagOptions.length - 1
      ) {
        setTagActiveIndex(currentTagActiveIndex + 1);
        return;
      }
      if (e.key === 'ArrowUp' && currentTagActiveIndex > 0) {
        setTagActiveIndex(currentTagActiveIndex - 1);
        return;
      }
    }
  };

  const handlePaste = (e: ClipboardEvent): void => {
    if (!getAllowCustomTag()) {
      return;
    }

    let pasteText: string = e.clipboardData?.getData('text/plain') || '';

    // Detecting newlines
    pasteText = pasteText.replace(
      /\n/g,
      String.fromCharCode(+getSeparator()[0])
    );

    // Removing the spaces
    pasteText = pasteText.replace(/\s/g, '');

    // Splitting pasted string as array based on separator prop
    getSeparator().forEach(
      (code) =>
        (pasteText = pasteText.split(String.fromCharCode(+code)).join('||'))
    );

    // Skipping the selectedTags
    const existingTags = getSelectedTagList().map((tag) => tag.value);
    const tagString: string[] = pasteText
      .split('||')
      .filter((tag) => !existingTags.includes(tag) && tag.length > 0);

    // Making array of tags from array of strings
    const pastTags: ITagItem[] = tagString.map((tag) => ({
      label: tag,
      value: tag,
      isValid: props.validateRx ? props.validateRx.test(tag) : true,
    }));

    const allTags: ITagItem[] = [...getSelectedTagList(), ...pastTags];
    onTagChange(allTags);
    e.preventDefault();
  };

  createEffect(() => {
    if (!props.setFocus) {
      setShowSetFocusLine(false);
      return;
    }
    setShowSetFocusLine(true);
    requestAnimationFrame(() => focusTagEditorInput());
  });

  onMount(() => {
    let cancelled = false;

    Promise.all([
      customElements.whenDefined('modus-wc-text-input'),
      customElements.whenDefined('modus-wc-chip'),
    ]).then(() => {
      if (cancelled) {
        return;
      }
      requestAnimationFrame(setupModusInput);
    });

    onCleanup(() => {
      cancelled = true;
      detachInputListeners?.();
    });
  });

  const handleSearchClose = (): void => {
    // Call search close callback if provided
    if (props.onSearchClose) {
      props.onSearchClose();
    }
    setSearchKey('');
  };

  // Computed values
  const errorMessage = (): string =>
    errMessage() || props.customErrorMessage || '';
  const tagOptions = (): ITagItem[] => getTagOptions();
  const notFoundErrorMsg = (): string =>
    getNotFoundErrorMsg().replace('{{userSearch}}', searchKey());
  const selectedInputCount = (): number =>
    props.inputCount?.selectedCount ?? getSelectedTagList().length;
  const totalInputCount = (): number | undefined =>
    props.inputCount?.totalCount;
  const inputCounter = (): string =>
    `Selected ${selectedInputCount().toString()}/${(totalInputCount() ?? 1).toString() || '∞'}`; // TODO: using 1 as fallback to avoid division by zero

  const shouldShowTagItems = (): boolean => {
    const currentSearchKey = searchKey();
    const currentTagOptions = tagOptions();
    const predefinedTagList = props.predefinedTagList;

    return !!(
      (currentSearchKey && predefinedTagList) ||
      (currentSearchKey &&
        (!getAllowCustomTag() ||
          props.notFoundErrorMsg ||
          currentTagOptions.length > 0))
    );
  };

  const getTagItems = (): ITagItem[] => {
    if (props.predefinedTagList && props.predefinedTagList.length > 0) {
      return props.predefinedTagList;
    }
    return tagOptions();
  };

  const maxLengthErrorText = (): string =>
    props.maxLengthErrorMessage ??
    `Maximum ${props.maxLength ?? 0} characters allowed.`;

  return (
    <div ref={hostRef} class="input-group tags">
      <div class="mc-tag-editor-header input-group mb-0">
        <Show when={props.title}>
          <label id="tageditor-title">{props.title}</label>
        </Show>
        <Show when={props.inputCount}>
          <label id="input-count">({inputCounter()})</label>
        </Show>
      </div>
      <div
        class={`group-items input-focus-group ${
          props.enableSearchIcon ? 'tag-spacing tags-single-lin' : ''
        } ${errorMessage() ? 'error' : ''} ${
          showSetFocusLine() ? 'mc-tag-editor-set-focus' : ''
        }`}
      >
        <Show when={props.enableSearchIcon}>
          <button class="m-1 ml-1" type="button">
            <modus-wc-icon name="search" size="sm" />
          </button>
        </Show>
        <div
          class={`mc-tag-editor-input-area ${errorMessage() ? 'error' : ''}`}
        >
          <ul class="mc-tag-editor-chip-row te-mw-full">
            <InputChips onRemoveTag={onRemoveTag} tags={getSelectedTagList()} />
            <li class="mc-tag-editor-input-cell">
              <div ref={modusInputWrapper} class="mc-tag-editor-input-wrap">
                <modus-wc-text-input
                  value={searchKey()}
                  placeholder={props.placeHolder}
                  type="text"
                  size="md"
                  bordered={false}
                  input-id={props.id}
                  max-length={props.maxLength}
                  auto-complete="off"
                  custom-class="mc-tag-editor-input"
                  data-cy={`${props.title?.toLowerCase() ?? 'untitled'}-input`}
                  on:inputChange={handleModusInputChange}
                  on:inputBlur={() => {
                    setShowSetFocusLine(false);
                    if (props.autoSave) {
                      saveTag();
                    }
                  }}
                />
              </div>
            </li>
          </ul>
          <Show
            when={props.maxLength && searchKey().length === props.maxLength}
          >
            <label class="error-message">{maxLengthErrorText()}</label>
          </Show>
          <Show when={shouldShowTagItems()}>
            <TagItems
              onAddTag={onAddTag}
              search={searchKey()}
              activeIndex={tagActiveIndex()}
              tags={getTagItems()}
              isLoading={loading()}
              notFoundErrorMsg={notFoundErrorMsg()}
              matchCase={getMatchCase()}
              enableSearchIcon={props.enableSearchIcon}
              tagListTitle={props.tagListTitle}
            />
          </Show>
          <div class="mc-tag-editor-focus-line" aria-hidden="true" />
        </div>
        <Show when={props.enableSearchIcon}>
          <button class="m-1 mr-1" type="button" onClick={handleSearchClose}>
            <modus-wc-icon name="close" size="sm" />
          </button>
        </Show>
      </div>
      <Show when={errorMessage()}>
        <div class="text-sm text-danger mt-1" textContent={errorMessage()} />
      </Show>
    </div>
  );
};

export default TagEditor;
